import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material/';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import TouchButton from '../../../components/TouchButton/TouchButton';
import toast from 'react-hot-toast';

// Matches the sold list next to it, so switching tabs does not resize the modal
const TABLE_HEIGHT = '46vh';
const SKELETON_ROWS = 8;

function ModalKioskStock({ onBack }) {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);
  const { hall, operator } = useContext(AppContext);

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  // Which product is being filled, and with how many
  const [editing, setEditing] = useState(null);
  const [qty, setQty] = useState('');

  useEffect(() => {
    if (!socket) return;

    const handleState = (response) => {
      if (!response || response.ok === false) {
        setFailed(true);
        return;
      }
      setFailed(false);
      setData(response);
    };

    const handleMove = (response) => {
      if (!response || response.ok === false) {
        toast.error(
          response?.reason === 'qtyRequired'
            ? t('sales.kioskStockQtyRequired')
            : t('error.error')
        );
        return;
      }
      toast.success(t('sales.kioskStockDone'));
      setEditing(null);
      setQty('');
      handleState(response);
    };

    socket.on('kioskStockState', handleState);
    socket.on('kioskStockMove', handleMove);
    socket.emit('kioskStockState', { bingoName: hall });

    return () => {
      socket.off('kioskStockState', handleState);
      socket.off('kioskStockMove', handleMove);
    };
  }, [socket, hall]);

  const submit = () => {
    const value = Number(qty);
    if (qty === '' || !Number.isFinite(value) || value <= 0) {
      toast.error(t('sales.kioskStockQtyRequired'));
      return;
    }

    socket.emit('kioskStockMove', {
      bingoName: hall,
      operator,
      kioskID: editing.kioskID,
      type: 'in',
      qty: value,
    });
  };

  const headerCellSx = {
    color: '#8fa3d4 !important',
    fontSize: '17px',
    fontWeight: 700,
    backgroundColor: '#151d3b',
  };
  const bodyCellSx = { color: '#e6eaf2 !important', fontSize: '19px' };
  const numberSx = { ...bodyCellSx, fontVariantNumeric: 'tabular-nums' };

  const items = data?.items || [];

  return (
    <>
      <h1>{t('sales.kioskStockTitle')}</h1>

      {editing ? (
        <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
          <Typography sx={{ color: '#e6eaf2', fontSize: '24px', mb: 1 }}>
            {editing.name}
          </Typography>
          <Typography sx={{ color: '#8fa3d4', fontSize: '17px', mb: 2 }}>
            {t('sales.kioskStockCurrent', { count: editing.stock })}
          </Typography>
          <TextField
            autoFocus
            autoComplete="off"
            type="number"
            label={t('sales.kioskStockQty') + '*'}
            value={qty}
            onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
            variant="outlined"
            className="modal-textfield"
            inputProps={{ min: 1, style: { fontSize: '28px' } }}
            style={{ width: '260px' }}
          />

          <div className="react-confirm-alert-button-group">
            <button className="btn-custom-yes" onClick={submit}>
              {t('sales.confirm')}
            </button>
            <button
              className="btn-custom-back"
              onClick={() => {
                setEditing(null);
                setQty('');
              }}
            >
              <ArrowBackIcon />
              {t('sales.openShiftBack')}
            </button>
          </div>
        </Box>
      ) : (
        <>
          <TableContainer
            sx={{
              height: TABLE_HEIGHT,
              overflowY: 'auto',
              backgroundColor: '#151d3b',
              borderRadius: '14px',
              border: '1px solid #2a3a6e',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    {t('sales.kioskProductsProduct')}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {t('sales.kioskStockReceived')}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {t('sales.kioskStockSold')}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    {t('sales.kioskStockOnHand')}
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failed && (
                  <TableRow>
                    <TableCell colSpan={5} sx={bodyCellSx}>
                      {t('error.error')}
                    </TableCell>
                  </TableRow>
                )}
                {!failed &&
                  !data &&
                  Array.from(new Array(SKELETON_ROWS)).map((_, row) => (
                    <TableRow key={row}>
                      {[0, 1, 2, 3, 4].map((col) => (
                        <TableCell
                          key={col}
                          align={col === 0 ? 'left' : 'right'}
                        >
                          <Skeleton
                            variant="text"
                            height={30}
                            width={col === 0 ? '60%' : '40%'}
                            sx={{
                              bgcolor: '#232a45',
                              ml: col === 0 ? 0 : 'auto',
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                {!failed && data && !items.length && (
                  <TableRow>
                    <TableCell colSpan={5} sx={bodyCellSx}>
                      {t('sales.kioskStockEmpty')}
                    </TableCell>
                  </TableRow>
                )}
                {items.map((item) => (
                  <TableRow key={item.kioskID}>
                    <TableCell sx={bodyCellSx}>
                      {item.name}
                      {/* Never stocked here: the figure is not a zero, it is unknown */}
                      {item.received === 0 && item.sold === 0 && (
                        <Typography sx={{ color: '#8fa3d4', fontSize: '14px' }}>
                          {t('sales.kioskStockUntracked')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={numberSx}>
                      {item.received}
                    </TableCell>
                    <TableCell align="right" sx={numberSx}>
                      {item.sold}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...numberSx,
                        fontWeight: 700,
                        color:
                          item.stock < 0
                            ? '#fcad89 !important'
                            : bodyCellSx.color,
                      }}
                    >
                      {item.stock}
                    </TableCell>
                    <TableCell align="right">
                      {/* Row-sized like the other table screens, not a menu tile */}
                      <TouchButton
                        tone="confirm"
                        sx={{
                          minWidth: 130,
                          height: 52,
                          fontSize: 17,
                          px: 1.5,
                        }}
                        onClick={() => {
                          setEditing(item);
                          setQty('');
                        }}
                      >
                        {t('sales.kioskStockFill')}
                      </TouchButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography sx={{ color: '#8fa3d4', fontSize: '15px', mt: 1.5 }}>
            {t('sales.kioskStockHelp')}
          </Typography>

          <div
            style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}
          >
            <button className="btn-custom-no" onClick={onBack}>
              <ArrowBackIcon />
              {t('sales.openShiftBack')}
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default ModalKioskStock;
